import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

/**
 * Low-level standalone radio input for callers that want to build their own radio
 * layout (e.g., inline radios inside a custom card grid). Most consumers should use
 * `RadioGroupComponent` instead, which manages group semantics and CVA automatically.
 *
 * @feature shared
 */
@Component({
  selector: 'app-radio-input',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'inline-flex items-center gap-2' },
  template: `
    <input
      type="radio"
      [name]="name()"
      [value]="value()"
      [checked]="checked()"
      [disabled]="disabled()"
      (change)="onChange()"
      class="h-4 w-4 accent-[var(--ion-color-primary)] focus-visible:outline-none focus-visible:shadow-[var(--theme-focus-ring)]"
    />
    @if (label()) {
      <span class="text-body">{{ label() }}</span>
    }
  `,
})
export class RadioInputComponent {
  /** The name attribute (groups related radios). */
  readonly name = input.required<string>();

  /** Value emitted on selection. */
  readonly value = input.required<string>();

  /** Whether this radio is selected. */
  readonly checked = input<boolean>(false);

  /** Disables the input. */
  readonly disabled = input<boolean>(false);

  /** Optional visible label next to the radio. */
  readonly label = input<string | undefined>(undefined);

  /** Fires when the radio becomes selected. */
  readonly selected = output<string>();

  onChange(): void {
    this.selected.emit(this.value());
  }
}
